import { ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";

export default function Hero() {
  const { t, pick } = useI18n();
  const { site } = useSite();

  if (!site) return null;
  const bg = site.hero_bg_url;
  const wa = site.whatsapp_number?.replace(/\D/g, "");

  return (
    <section id="top" data-testid="hero" className="relative overflow-hidden border-b border-zinc-900">
      {/* Background */}
      <div className="absolute inset-0">
        {bg && <img src={bg} alt="" className="w-full h-full object-cover opacity-60" />}
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_50%,rgba(230,57,70,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_10%,rgba(244,244,245,0.05),transparent_50%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-28 lg:pt-24 lg:pb-36">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* LEFT: text */}
          <div className="lg:col-span-7 text-white order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 border border-brand-red/60 bg-brand-red/10">
              <span className="block h-1.5 w-1.5 bg-brand-red animate-pulse" />
              <span className="tech-label !text-[10px] !text-brand-red">
                {t("hero.tagline")}
              </span>
            </div>

            <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              <span className="block chrome-text">{pick(site.hero_title)}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed">
              {pick(site.hero_subtitle)}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              {wa && (
                <a
                  data-testid="hero-cta-whatsapp"
                  href={`https://wa.me/${wa}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover text-white px-6 py-4 font-bold text-base transition-colors duration-150 hard-shadow"
                >
                  <MessageCircle size={18} />
                  {t("hero.secondaryCta")}
                </a>
              )}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-zinc-500">
              <span className="flex items-center gap-2">
                <span className="block h-1.5 w-1.5 bg-brand-red" /> {t("hero.trust")}
              </span>
            </div>
          </div>

          {/* RIGHT: Logo + info card */}
          <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col items-center lg:items-end gap-6">
            {/* DYNAMIC LOGO */}
            {site.logo_url ? (
              <img
                data-testid="hero-logo"
                src={site.logo_url}
                alt={site.business_name}
                className="w-full max-w-[320px] sm:max-w-[400px] lg:max-w-[440px] h-auto object-contain drop-shadow-[0_8px_30px_rgba(230,57,70,0.35)]"
              />
            ) : (
              <div className="w-44 h-44 sm:w-56 sm:h-56 bg-black border-2 border-brand-red flex items-center justify-center text-white font-display font-black text-5xl drop-shadow-[0_8px_30px_rgba(230,57,70,0.35)]">
                GBS
              </div>
            )}

            <div className="w-full max-w-[440px] bg-zinc-950/80 backdrop-blur-sm border border-zinc-800 border-l-4 border-l-brand-red p-5 sm:p-6">
              <div className="grid grid-cols-2 gap-4">
                <a href={`tel:${site.phone_primary}`} className="block group">
                  <div className="tech-label !text-[10px] mb-0.5">{t("contact.phoneLabel")}</div>
                  <div className="font-display font-extrabold text-lg sm:text-xl tracking-tight chrome-text group-hover:opacity-80">{site.phone_primary}</div>
                </a>
                {site.phone_secondary && (
                  <a href={`tel:${site.phone_secondary}`} className="block group">
                    <div className="tech-label !text-[10px] mb-0.5">Alt.</div>
                    <div className="font-display font-bold text-lg sm:text-xl tracking-tight chrome-text group-hover:opacity-80">{site.phone_secondary}</div>
                  </a>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="tech-label !text-[10px] mb-1 flex items-center gap-1"><MapPin size={12} /> {t("contact.addressLabel")}</div>
                <div className="text-sm font-medium text-zinc-200">{site.address}</div>
              </div>
              <div className="mt-3">
                <div className="tech-label !text-[10px] mb-1">{t("contact.hoursLabel")}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{pick(site.hours)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
